import React, { useState, useEffect, useRef } from 'react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { AlertCircle, Plus, Eye, Clock, FileText } from 'lucide-react';
import { Box, Button } from '@mui/material';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { DndContext, closestCorners, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import QuoteForm from './QuoteForm';
import QuoteDetailsModal from './QuoteDetailsModal';
import './KanbanBoard.css';

const SortableQuoteCard = ({ quote, isOverThreshold, thresholds, onClick }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: quote.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1 : 0
    };

    const formattedPrice = quote.price_inc_tax ? `$${Number(quote.price_inc_tax).toLocaleString()}` : '';
    const locationText = quote.suburb || (quote.site_name ? quote.site_name.split(',').pop()?.trim() : '');
    const salesperson = quote.salesperson || '';

    // Determine quote type class for styling based on original status
    const getQuoteTypeClass = (status) => {
        if (status?.includes('Office Quote')) return 'office-quotes';
        if (status?.includes('On Site') || status?.includes('Onsite')) return 'onsite-consultations';
        if (status?.includes('Sent to Client')) return 'price-estimates';
        if (status?.includes('New Lead')) return 'new-leads';
        return '';
    };

    const quoteTypeClass = getQuoteTypeClass(quote.status);

    return (
        <div ref={setNodeRef} style={style} className={`quote-card ${quoteTypeClass} ${isOverThreshold ? 'over-threshold' : ''} ${quote.isHighValue ? 'high-value' : ''} ${isDragging ? 'dragging' : ''}`} {...attributes}>
            <div className="quote-header" {...listeners}>
                <span className="quote-id">{quote.id}</span>
                <div>
                    {quote.isHighValue && <span className="high-value-indicator" title="High Value Quote">💎</span>}
                    {isOverThreshold && <span className="threshold-indicator"><AlertCircle size={16} /></span>}
                </div>
            </div>
            <div className="quote-customer">{quote.customer}</div>
            <div className="quote-tags">
                {formattedPrice && <span className="tag tag-price">{formattedPrice}</span>}
                {locationText && <span className="tag tag-location" title={locationText}>{locationText}</span>}
                {salesperson && <span className="tag tag-salesperson" title={`Salesperson: ${salesperson}`}>{salesperson}</span>}
            </div>
            <div className="quote-footer">
                <div className="days-in-status">
                    <Clock size={14} />
                    <span>{quote.daysInStatus} days</span>
                </div>
                {quote.latestUpdate && <div className="latest-update">{format(new Date(quote.latestUpdate), 'MMM d')}</div>}
            </div>
            {isOverThreshold && (
                <div className="threshold-alert">
                    <AlertCircle size={14} />
                    <span>Exceeds {thresholds[quote.status] || 0} day threshold</span>
                </div>
            )}
            <button
                className="details-button"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();  // ✅ already have this
                    e.nativeEvent.stopImmediatePropagation();  // 🛑 ADD THIS LINE
                    console.log('View details button clicked for quote:', quote.id);
                    onClick(quote);
                }}
            >
                <Eye size={14} style={{ marginRight: '4px' }} />
                View Details
            </button>
        </div>
    );
};

const KanbanBoard = ({ quotes = [], logs = [], onStatusChange, onAddQuote, onAddLog, onUpdateQuote }) => {
    const [columns, setColumns] = useState({});
    const [activeId, setActiveId] = useState(null);
    const [isQuoteFormOpen, setIsQuoteFormOpen] = useState(false);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const dialogRef = useRef(null);

    console.log('🔎 ModalOpen:', modalOpen, 'SelectedQuote:', selectedQuote);

    const statusCategories = [
        'New Leads', // includes New Lead Podium
        'Office Quotes',
        'On-Site Consultations',
        'Quotes Sent',
        'Quotes Won',
        'Jobs Scheduled',
        'Jobs In Progress',
        'Jobs Completed',
        'Quotes On Hold',
        'Quotes Archived',
        'Follow Ups',
        'Other'
    ];

    const statusMapping = {
        'Quote: New Lead Podium texted awaiting response': 'New Leads',
        'Quote : Office Quote': 'Office Quotes',
        'Quote : On Site Sales Consultation': 'On-Site Consultations',
        'Quote : Onsite Consultation Tomorrow': 'On-Site Consultations',
        'Quote : Sent to Client': 'Quotes Sent',
        'Quote : Sent to Client 30%': 'Quotes Sent',
        'Quote : Sent to Client 60%': 'Quotes Sent',
        'Quote : Sent to Client 90%': 'Quotes Sent',
        'Quote Won': 'Quotes Won',
        'Quote : Won - Technical site inspection Booked': 'Quotes Won',
        'Job : Deposit Invoice Required': 'Jobs Scheduled',
        'Job: Booking to be confirmed by customer': 'Jobs Scheduled',
        'Job : Follow up Required': 'Jobs In Progress',
        'Job : Installation Manager Action Required': 'Jobs In Progress',
        'Job : New Maintenance Job': 'Jobs In Progress',
        'Job : New Service Job': 'Jobs In Progress',
        'Job : Parts To Be Ordered': 'Jobs In Progress',
        'Job : Completed': 'Jobs Completed',
        'Job Closed - Other': 'Jobs Completed',
        'Quote .. on hold awaiting client': 'Quotes On Hold',
        'Quote : On Hold': 'Quotes On Hold',
        'Quote : Archived': 'Quotes Archived',
        'Quote : Follow Up Salesperson': 'Follow Ups',
        'Quote : No Action Just Looking For Now': 'Other',
        'Quote : Other Chosen': 'Other',
        'Quote: Installation Manager To Follow Up': 'Follow Ups'
    };


    const thresholds = { 'New Lead': 3, 'Office Quote': 2, 'Quote On-Site': 5, 'Quote Travel to Site': 1, 'Quote Sent': 0, 'Quote Won': 2, 'Job - Invoice Deposit': 5 };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    useEffect(() => {
        const initialColumns = {};
        statusCategories.forEach(status => { initialColumns[status] = []; });

        quotes.forEach(quote => {
            const mappedStatus = statusMapping[quote.status] || 'Other';

            const quoteLogs = logs.filter(log => log.quote_id === quote.id);
            const lastStatusChange = quoteLogs.length > 0
                ? quoteLogs.sort((a, b) => new Date(b.date_logged) - new Date(a.date_logged))[0].date_logged
                : quote.date_issued;

            const daysInStatus = lastStatusChange
                ? differenceInDays(new Date(), parseISO(lastStatusChange))
                : 0;

            const threshold = thresholds[mappedStatus] || 0;
            const isOverThreshold = daysInStatus > threshold && threshold > 0;
            const latestUpdate = lastStatusChange;

            initialColumns[mappedStatus].push({
                ...quote,
                daysInStatus,
                isOverThreshold,
                latestUpdate
            });
        });

        setColumns(initialColumns);

    }, [quotes, logs]);

    const [loadingQuote, setLoadingQuote] = useState(false);

    const handleQuoteClick = async (quote) => {
        console.log('handleQuoteClick called with quote:', quote.id);

        setLoadingQuote(true);
        try {
            const res = await fetch(`/api/quotes/${quote.id}`);
            const fullQuote = await res.json();
            setSelectedQuote(fullQuote);
            setModalOpen(true);
        } catch (error) {
            console.error('Failed to fetch full quote:', error);
        } finally {
            setLoadingQuote(false);
        }
    };

    // Function to handle exporting quotes to Excel
    const handleExportToExcel = () => {
        // Flatten all quotes from all columns into a single array
        const allQuotes = [];
        Object.keys(columns).forEach(status => {
            columns[status].forEach(quote => {
                // Add the category/status to each quote
                allQuotes.push({
                    ...quote,
                    category: status
                });
            });
        });

        // Create a new workbook
        const workbook = XLSX.utils.book_new();

        // Convert quotes array to worksheet
        const worksheet = XLSX.utils.json_to_sheet(allQuotes);

        // Add the worksheet to the workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Quotes');

        // Generate Excel file
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

        // Create a Blob from the buffer
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

        // Save the file
        saveAs(blob, `eclipse_dash_quotes_${new Date().toISOString().split('T')[0]}.xlsx`);
    };


    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over) { setActiveId(null); return; }
        let sourceColumnId;
        let quote;
        for (const status of statusCategories) {
            const quoteIndex = columns[status]?.findIndex(q => q.id === active.id);
            if (quoteIndex >= 0) {
                sourceColumnId = status;
                quote = columns[status][quoteIndex];
                break;
            }
        }
        if (!sourceColumnId || !quote) { setActiveId(null); return; }
        const destinationEl = over.data.current?.sortable?.containerId || sourceColumnId;
        if (sourceColumnId !== destinationEl) {
            const newSourceColumn = columns[sourceColumnId].filter(q => q.id !== active.id);
            const newDestColumn = [...(columns[destinationEl] || []), { ...quote, status: destinationEl, daysInStatus: 0, isOverThreshold: false }];
            setColumns({ ...columns, [sourceColumnId]: newSourceColumn, [destinationEl]: newDestColumn });
            if (onStatusChange) { onStatusChange(active.id, destinationEl); }
        }
        setActiveId(null);
    };

    return (
        <div className="kanban-board">
            {/* Standalone Export Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                    variant="contained"
                    color="success"
                    startIcon={<FileText size={16} />}
                    onClick={handleExportToExcel}
                    sx={{ fontWeight: 'bold', padding: '10px 20px', fontSize: '16px' }}
                >
                    Export All Quotes to Excel
                </Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4, justifyContent: 'space-between' }}>
                <Button variant="contained" color="primary" startIcon={<Plus size={16} />} onClick={() => setIsQuoteFormOpen(true)}>New Quote</Button>
            </Box>
            <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
                <div className="kanban-columns">
                    {statusCategories.map(status => (
                        <div key={status} className="kanban-column">
                            <div className="column-header">
                                <h3>{status}</h3>
                                <span className="quote-count">{columns[status]?.length || 0}</span>
                                {columns[status]?.filter(q => q.isOverThreshold).length > 0 && <span className="alert-badge"><AlertCircle size={16} /></span>}
                            </div>
                            <div className="column-content" data-column={status}>
                                <SortableContext items={columns[status]?.map(q => q.id) || []} strategy={verticalListSortingStrategy} id={status}>
                                    {columns[status]?.map(quote => (
                                        <SortableQuoteCard key={quote.id} quote={quote} isOverThreshold={quote.isOverThreshold} thresholds={thresholds} onClick={handleQuoteClick} />
                                    ))}
                                </SortableContext>
                            </div>
                        </div>
                    ))}
                </div>
                <DragOverlay>
                    {activeId && <div className="quote-card dragging">Dragging...</div>}
                </DragOverlay>
            </DndContext>
            <QuoteForm open={isQuoteFormOpen} onClose={() => setIsQuoteFormOpen(false)} onSubmit={(q, log) => onAddQuote(q, log)} />
            {loadingQuote && <div className="loading-spinner">Loading...</div>}
            {modalOpen && selectedQuote && (
                <QuoteDetailsModal
                    open={modalOpen}
                    quote={selectedQuote}
                    onClose={() => { setModalOpen(false); setTimeout(() => setSelectedQuote(null), 100); }}
                />
            )}

        </div>
    );
};

export default KanbanBoard;
